import { SearchOutlined } from '@ant-design/icons';
import { Button, Input, Table, Card, Empty } from 'antd';
import { useState, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import ListIcon from '@mui/icons-material/List';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import axios from 'axios';
import { useCookies } from 'react-cookie'
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getBizID } from '../../libs/todoSlice';
import LoadingComponent from '../../lib/LoadingComponent';
import FadeIn from 'react-fade-in'
import { Chip } from '@mui/material';

import {
  setEmployeesRedux,
  getEmployeesRedux,
  getLoadingRedux,
  setLoadingRedux,
  getCountEmployeesRedux,
  setCountEmployeesRedux,
} from '../../libs/manageMemberSlice';

// อ่าน list จาก env แบบไม่ทำให้หน้า crash ถ้าค่าเป็น JSON เสีย
const parseEnvList = (value) => {
  try {
    return value ? JSON.parse(value) : []
  } catch {
    return []
  }
}
const listIdCardCheck = parseEnvList(process.env.REACT_APP_HIDDEN_ID_CARD)
const listEmpIdCheck = parseEnvList(process.env.REACT_APP_EMP_ID)

// hover ของการ์ด: ใช้ currentTarget (ตัวการ์ดเอง) ไม่ใช่ target (element ลูกที่เมาส์ชี้อยู่)
const hoverIn = ({ currentTarget }) => {
  currentTarget.style.transform = 'scale(1.1)'
  currentTarget.style.transition = 'all .5s'
}
const hoverOut = ({ currentTarget }) => {
  currentTarget.style.transform = 'scale(1.0)'
}

const customLocale = {
  emptyText: (<Empty description="ไม่มีข้อมูล" />)
};

const Member = () => {
  const [cookie] = useCookies(['account'])
  const params = useParams()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchText, setSearchText] = useState('')   // ข้อความในช่องพิมพ์
  const [keyword, setKeyword] = useState('')         // ข้อความที่ "ยืนยันค้นหาแล้ว" (ใช้ยิง API)
  const [onlyNewData, setOnlyNewData] = useState(false)
  const [new_user_count, setNew_User_Count] = useState('')
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const business_id = useSelector(getBizID)

  const employees = useSelector(getEmployeesRedux)
  const setEmployees = (action) => {
    dispatch(setEmployeesRedux({
      employees: action
    }))
  }

  const countEmployees = useSelector(getCountEmployeesRedux)
  const setCountEmployees = (action) => {
    dispatch(setCountEmployeesRedux({
      countEmployees: action
    }))
  }

  const loading = useSelector(getLoadingRedux)
  const setLoading = (action) => {
    dispatch(setLoadingRedux({
      loading: action
    }))
  }

  useEffect(() => {
    const hasAccount = !!localStorage.getItem('account')

    if (!hasAccount) {
      navigate(`/${params.prefix}`, { replace: true })
      return
    }

    if (cookie.new_user && !cookie.new_user.changed_password) {
      navigate(`/${params.prefix}`, { replace: true })
    }
  }, [cookie.new_user, params.prefix, navigate])

  const getEmployees = async (signal) => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v6/businesses/employees`, {
        params: {
          biz_id: business_id,
          page: page,
          perPage: pageSize,
          searching: keyword,
          only_new_data: onlyNewData
        },
        signal,
      })
      const prepare_data = data.data.list.map((employee, index) => ({
        index: index + 1,
        id_card_num: employee.id_card_num,
        account_id: employee.id,
        emp_id: employee.emp_id ?? '-',
        tel: employee.mobile_no,
        email: employee.email,
        status: 'active',
        name_th: `${employee.special_title_th || employee.account_title_th || ''} ${employee.first_name_th || ''} ${employee.last_name_th || ''}`,
        name_eng: `${employee.special_title_eng || employee.account_title_eng || ''} ${employee.first_name_eng || ''} ${employee.last_name_eng || ''}`,
        username: employee.username ?? '-',
      }))
      setCountEmployees(data.data.total)
      setEmployees(prepare_data)
      setNew_User_Count(data.data.new_user)
    } catch (error) {
      // ถูกยกเลิกเพราะมี request ใหม่แทน: ไม่ต้องปิด loading (request ใหม่จะปิดเอง)
      if (axios.isCancel(error)) return
      const status = error.response?.status
      if (status === 401) {
        navigate(`/${params.prefix}`)
      } else if (status === 403) {
        navigate(`/forbidden`)
      }
    }
    setLoading(false)
  }

  useEffect(() => {
    setLoading(true)
    setSearchText('')
    setOnlyNewData(false)
  }, [])

  // ที่เดียวที่ยิง API: เปลี่ยนหน้า / ขนาดหน้า / การ์ด / คำค้นที่ยืนยันแล้ว
  // abort request เก่าเมื่อมี request ใหม่ กันผลลัพธ์ตอบกลับมาสลับลำดับ
  useEffect(() => {
    if (!business_id) return
    const controller = new AbortController()
    getEmployees(controller.signal)
    return () => controller.abort()
  }, [page, pageSize, onlyNewData, keyword, business_id])

  const doSearch = () => {
    setPage(1)
    setKeyword(searchText.trim())
  }

  const columns = [
    {
      title: 'ลำดับ',
      width: 50,
      dataIndex: 'index',
      key: 'index',
      fixed: 'left',
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
      render: (_, __, index) => {
        return (
          <>{((page * pageSize) - pageSize) + index + 1}</>
        )
      }
    },
    {
      title: 'รหัสพนักงาน',
      dataIndex: 'emp_id',
      width: 150,
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
      hidden: !listEmpIdCheck.includes(business_id),
    },
    {
      title: (
        <div>
          ชื่อ - นามสกุล <br />(ภาษาไทย)
        </div>
      ),
      dataIndex: 'name_th',
      key: 'name_th',
      width: 150,
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
    },
    {
      title: (
        <div>
          ชื่อ - นามสกุล <br />(ภาษาอังกฤษ)
        </div>
      ),
      dataIndex: 'name_eng',
      key: 'name_eng',
      width: 150,
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
    },
    {
      title: 'หมายเลขบัตรประชาชน',
      width: 150,
      dataIndex: 'id_card_num',
      key: 'id_card_num',
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
      hidden: listIdCardCheck.includes(business_id),
      render: (_) => (_ !== 'DUMMY' ? _ : 'ไม่มีข้อมูล'),
    },
    {
      title: 'เบอร์โทร',
      dataIndex: 'tel',
      key: 'tel',
      width: 150,
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
    },
    {
      title: 'อีเมล',
      dataIndex: 'email',
      key: 'email',
      width: 170,
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
    },
    {
      title: 'Username',
      dataIndex: 'username',
      width: 120,
      render: (username) => username ?? '-',
    },
    {
      title: (<div>Status</div>),
      dataIndex: 'status',
      key: 'status',
      width: 120,
      responsive: ["xs", "sm", "md", "lg", "xl", "xxl"],
      render: (_) => {
        if (_ === 'active') {
          return (<Chip label="active" variant='outlined' color="success" />)
        }
        return (<Chip label="revoked" variant='outlined' color="error" />)
      }
    },
  ].filter(item => !item.hidden);

  return (
    <FadeIn>
      {
        loading ?
          <LoadingComponent />
          :
          <>
            <Row>
              <Col xl={3} lg={4} md={6} sm={12} xs={12}>
                <Card
                  style={{
                    width: '100%',
                    borderRadius: '16px',
                    marginRight: '20px',
                    marginBottom: '10px',
                    cursor: 'pointer',
                    backgroundImage: onlyNewData === false ? 'linear-gradient( transparent -10%, #3776E9 150%)' : 'none',
                  }}
                  onMouseEnter={hoverIn}
                  onMouseLeave={hoverOut}
                  onClick={() => {
                    setOnlyNewData(false)
                    setPage(1)
                  }}
                >
                  <Row>
                    <Col className='align-self-center' style={{ textAlign: 'center' }}>
                      <ListIcon style={{
                        backgroundColor: '#3776E9',
                        borderRadius: '50%',
                        fontSize: '52px',
                        padding: '5px',
                        color: '#fff',
                      }} />
                    </Col>
                    <Col className='align-self-center' style={{ textAlign: 'center' }}>
                      <span style={{
                        fontSize: '31px',
                        color: '#3776E9',
                        fontWeight: '700'
                      }}>
                        {countEmployees}
                      </span>
                      <br />
                      สมาชิกทั้งหมด
                    </Col>
                  </Row>
                </Card>
              </Col>
              <Col xl={3} lg={4} md={6} sm={12} xs={12}>
                <Card
                  style={{
                    width: '100%',
                    borderRadius: '16px',
                    cursor: 'pointer',
                    backgroundImage: onlyNewData === true ? 'linear-gradient( transparent -10%, #053075 160%)' : 'none',
                  }}
                  onMouseEnter={hoverIn}
                  onMouseLeave={hoverOut}
                  onClick={() => {
                    setOnlyNewData(true)
                    setPage(1)
                  }}
                >
                  <Row>
                    <Col className='align-self-center' style={{ textAlign: 'center' }}>
                      <PersonAddIcon style={{
                        backgroundColor: '#EFF4FD',
                        borderRadius: '50%',
                        fontSize: '52px',
                        padding: '5px',
                        color: '#053075',
                      }} />
                    </Col>
                    <Col className='align-self-center' style={{ textAlign: 'center' }}>
                      <span style={{
                        fontSize: '31px',
                        color: '#3D5D85',
                        fontWeight: '700'
                      }}>
                        {new_user_count}
                      </span>
                      <br />
                      สมาชิกใหม่
                    </Col>
                  </Row>
                </Card>
              </Col>
            </Row>

            <Row style={{ marginBottom: '20px', marginTop: '20px' }}>
              <Col xl={12} lg={12} md={6} sm={12} xs={12}>
                <h5>ค้นหา</h5>
              </Col>
              <Col xl={6} lg={10} md={6} sm={12} xs={12}>
                <Input.Group compact>
                  <Input
                    value={searchText}
                    allowClear
                    size="large"
                    style={{
                      width: 'calc(100% - 40px)',
                    }}
                    placeholder="ค้นหาด้วยชื่อ-นามสกุล, เลขบัตรประจำตัวประชาชน หรือเลขที่สัญญา"
                    onChange={({ target }) => {
                      setSearchText(target.value);
                      // ล้างช่องค้นหา (กดกากบาท/ลบจนว่าง) -> กลับไปแสดงทั้งหมดทันที
                      if (!target.value && keyword) {
                        setPage(1)
                        setKeyword('')
                      }
                    }}
                    onPressEnter={doSearch}
                    maxLength={100}
                  />
                  <Button
                    icon={<SearchOutlined />}
                    size="large"
                    style={{ borderRadius: '0px 4px 4px 0px' }}
                    onClick={doSearch}
                  />
                </Input.Group>
              </Col>
            </Row>
            <Table
              locale={customLocale}
              columns={columns}
              dataSource={employees}
              rowKey="account_id"
              scroll={{
                x: 1400,
                y: 400,
              }}
              pagination={{
                current: page,
                pageSize: pageSize,
                onChange: (page, pageSize) => {
                  setPage(page)
                  setPageSize(pageSize)
                },
                total: countEmployees,
                showSizeChanger: true
              }}
            />
          </>
      }
    </FadeIn>
  );
}
export default Member